<template>
  <div class="fill-height">
    <line-chart
      v-if="viewMode === 'chart'"
      class="fill-height"
      :raw-data="removable ? processedWeeklyDataMock : processedWeeklyData"
      :chart-value-keys="chartValueKeys"
      x-value="day"
      :colors="['#8884d8', '#82ca9d', '#ffc658', '#ff8042']"
      :yaxis="[{ title: { text: $t('chart_legends.load') } }]"
      :xaxis="{
        type: 'category',
        categories: daysOfWeek,
      }"
      :fill="{ type: ['solid','solid','solid', 'gradient'], opacity: [0.3,0.3,0.3, 1], gradient: { inverseColors: false, shade: 'light', type: 'vertical', opacityFrom: 0.85, opacityTo: 0.55 } }"
    >
      <template v-slot:header>
        <v-card-title class="pa-6 d-flex justify-space-between">
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('card_title.weekly_change') }}: {{ $t(`variables.${selectedVariable}.long`) }}
          </div>
          <v-btn
            v-if="removable"
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
          <visual-list-toggle
            v-else
            :value.sync="viewMode"
          />
        </v-card-title>
      </template>
    </line-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-6 d-flex justify-space-between">
        <div class="subtitle-2 grey--text text--grey-100">
          {{ $t('card_title.weekly_change') }}: {{ $t(`variables.${selectedVariable}.long`) }}
        </div>
        <div class="d-flex align-center">
          <visual-list-toggle
            v-if="!removable"
            :value.sync="viewMode"
          />
        </div>
      </v-card-title>

      <v-data-table
        :headers="tableHeaders"
        :items="removable ? processedWeeklyDataMock : processedWeeklyData"
        hide-default-footer
        disable-pagination
        :items-per-page="-1"
        fixed-header
        dense
        :height="350"
      >
        <template v-slot:item.day="{ item }">
          {{ item.day }}
        </template>
        <template v-slot:item.week1="{ item }">
          {{ item.week1 !== null ? item.week1 : '-' }}
        </template>
        <template v-slot:item.week2="{ item }">
          {{ item.week2 !== null ? item.week2 : '-' }}
        </template>
        <template v-slot:item.week3="{ item }">
          {{ item.week3 !== null ? item.week3 : '-' }}
        </template>
        <template v-slot:item.week4="{ item }">
          {{ item.week4 !== null ? item.week4 : '-' }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import dayjs from 'dayjs'

export default {
  name: 'WeeklyLoadChart',
  components: { LineChart, VisualListToggle },
  props: {
    selectedVariable: {
      type: String,
      required: true
    },
    stats: {
      type: Array,
      required: true
    },
    currentDate: {
      type: Object,
      default: () => dayjs()
    },
    loading: {
      type: Boolean,
      default: false
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      weekLabels: ['Week -3', 'Week -2', 'Week -1', 'Current Week'],
      viewMode: 'chart'
    }
  },

  computed: {
    currentDateIndex() {
      const day = this.currentDate.day()
      return day === 0 ? 6 : day - 1
    },

    chartValueKeys() {
      return this.weekLabels.map((label, index) => ({
        key: `week${index + 1}`,
        type: label === 'Current Week' ? 'area' : 'line',
        label: label
      }))
    },

    tableHeaders() {
      return [
        {
          text: this.$t('table_headers.day'),
          value: 'day',
          sortable: false,
          width: '20%'
        },
        {
          text: 'Week -3',
          value: 'week1',
          sortable: true,
          align: 'center'
        },
        {
          text: 'Week -2',
          value: 'week2',
          sortable: true,
          align: 'center'
        },
        {
          text: 'Week -1',
          value: 'week3',
          sortable: true,
          align: 'center'
        },
        {
          text: 'Current Week',
          value: 'week4',
          sortable: true,
          align: 'center'
        }
      ]
    },

    processedWeeklyData() {
      const data = this.daysOfWeek.map(day => ({
        day,
        week1: null,
        week2: null,
        week3: null,
        week4: null
      }))

      if (!this.stats || this.stats.length === 0) {
        return data
      }

      for (let i = 0; i < this.stats.length && i < 28; i++) {
        const stat = this.stats[i]
        if (!stat || !stat.stats) continue

        const statDate = i === 0 ? this.currentDate : this.currentDate.subtract(i, 'day')
        const dayOfWeek = statDate.day()
        const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1

        const weekOffset = Math.floor((i + (6 - this.currentDateIndex)) / 7)
        const weekIndex = 4 - weekOffset

        if (weekIndex > 0 && weekIndex <= 4) {
          data[dayIndex][`week${weekIndex}`] = stat.stats[this.selectedVariable + '_DL']
        }
      }

      for (let i = this.currentDateIndex + 1; i < 7; i++) {
        data[i].week4 = null
      }

      return data
    },
    processedWeeklyDataMock() {
      const data = this.daysOfWeek.map(day => ({
        day,
        week1: null,
        week2: null,
        week3: null,
        week4: null
      }))

      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        for (let weekIndex = 1; weekIndex <= 4; weekIndex++) {
          if (weekIndex === 4 && dayIndex > this.currentDateIndex) {
            continue
          }

          const seed = (dayIndex + 1) * (weekIndex + 1)
          data[dayIndex][`week${weekIndex}`] = Math.round(Math.sin(seed) * 30 + 60)
        }
      }

      return data
    }
  }
}
</script>
